import { useState } from 'react';
import { useInView } from '../../hooks/useInView';

interface SectionFinalCTAProps {
  heading?: string;
  bgImage?: string;
}

export default function SectionFinalCTA({
  heading = 'Start Planning Your Event Today',
  bgImage = 'https://images.squarespace-cdn.com/content/v1/5ff52abbfe6e8d5e4781b57a/4af24bd9-4fc6-4d89-abe7-e71420a4fc3d/4I6A0440.jpg',
}: SectionFinalCTAProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { ref: leftRef, isVisible: leftVisible } = useInView();
  const { ref: rightRef, isVisible: rightVisible } = useInView({ threshold: 0.1 });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d717nq4991ove783sq10', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
    } catch {
      /* silently handled */
    }
    setSubmitted(true);
    setSubmitting(false);
  };

  return (
    <section id="contact" className="relative py-24 lg:py-32 overflow-hidden grain-overlay">
      {/* Background */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src={bgImage}
          alt="Red Door Ranch luxury event venue"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-rdr-dark/85 via-rdr-dark/80 to-rdr-dark/90" />
      </div>

      {/* Gold accent lines */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-rdr-gold/60 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-rdr-gold/40 to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Headline */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-4 mb-5">
            <span className="gold-divider" />
            <p className="text-rdr-gold text-xs tracking-[0.25em] uppercase font-sans">Begin Your Journey</p>
            <span className="gold-divider" />
          </div>
          <h2 className="font-serif text-white text-4xl lg:text-5xl xl:text-6xl mb-6 max-w-3xl mx-auto leading-tight">
            {heading}
          </h2>
          <p className="text-white/60 font-sans text-lg max-w-xl mx-auto leading-relaxed">
            We can&apos;t wait to make your dream event a reality. Schedule a private tour and let&apos;s begin crafting something extraordinary.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
          {/* Contact Info */}
          <div
            ref={leftRef as React.RefObject<HTMLDivElement>}
            className={`flex flex-col justify-center gap-10 slide-left ${leftVisible ? 'is-visible' : ''}`}
          >
            <div>
              <p className="text-rdr-gold text-xs tracking-[0.2em] uppercase font-sans mb-3">Call or Text</p>
              <a
                href="tel:+18175048501"
                className="font-serif text-white text-3xl lg:text-4xl hover:text-rdr-gold transition-colors duration-300 cursor-pointer block"
              >
                (817) 504-8501
              </a>
            </div>
            <div>
              <p className="text-rdr-gold text-xs tracking-[0.2em] uppercase font-sans mb-3">Visit the Ranch</p>
              <a
                href="https://www.google.com/maps/dir//5801+County+Rd+915,+Joshua,+TX+76058"
                target="_blank"
                rel="nofollow noreferrer"
                className="text-white/70 font-sans text-base hover:text-white transition-colors duration-300 leading-relaxed cursor-pointer block"
              >
                5801 County Road 915<br />Joshua, TX 76058
              </a>
            </div>
            <div>
              <p className="text-rdr-gold text-xs tracking-[0.2em] uppercase font-sans mb-3">Email</p>
              <a
                href="mailto:reddoorranchtx@gmail.com"
                className="text-white/70 font-sans text-base hover:text-white transition-colors duration-300 cursor-pointer block"
              >
                reddoorranchtx@gmail.com
              </a>
            </div>

            {/* Social */}
            <div className="flex items-center gap-3">
              {[
                { href: 'https://www.facebook.com/RedDoorRanchVenue', icon: 'ri-facebook-fill', label: 'Facebook' },
                { href: 'https://www.instagram.com/reddoorranchvenue', icon: 'ri-instagram-line', label: 'Instagram' },
                { href: 'https://www.pinterest.com/RedDoorRanchVenue/', icon: 'ri-pinterest-line', label: 'Pinterest' },
              ].map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="nofollow noreferrer"
                  aria-label={s.label}
                  className="w-10 h-10 flex items-center justify-center border border-white/20 hover:border-rdr-gold text-white/50 hover:text-rdr-gold transition-colors duration-300 cursor-pointer"
                >
                  <i className={`${s.icon} text-base`} />
                </a>
              ))}
            </div>
          </div>

          {/* Form */}
          <div
            ref={rightRef as React.RefObject<HTMLDivElement>}
            className={`slide-right ${rightVisible ? 'is-visible' : ''}`}
          >
            <div className="bg-white/8 backdrop-blur-sm border border-white/15 p-8 lg:p-10">
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-rdr-gold/20 rounded-full mb-6">
                    <i className="ri-check-line text-3xl text-rdr-gold" />
                  </div>
                  <h3 className="font-serif text-white text-2xl mb-3">Thank You!</h3>
                  <p className="text-white/60 font-sans text-sm leading-relaxed">
                    We&apos;ve received your inquiry and will be in touch within 24 hours to schedule your private tour of Red Door Ranch.
                  </p>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="rdr-tour-booking-form"
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <h3 className="font-serif text-white text-2xl mb-6">Schedule a Private Tour</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-white/50 text-xs uppercase tracking-widest font-sans block mb-2">First Name *</label>
                      <input
                        name="first_name"
                        type="text"
                        required
                        className="w-full bg-white/8 border border-white/15 text-white placeholder-white/25 px-4 py-3 text-sm font-sans focus:outline-none focus:border-rdr-gold transition-colors rounded-sm"
                        placeholder="First name"
                      />
                    </div>
                    <div>
                      <label className="text-white/50 text-xs uppercase tracking-widest font-sans block mb-2">Last Name *</label>
                      <input
                        name="last_name"
                        type="text"
                        required
                        className="w-full bg-white/8 border border-white/15 text-white placeholder-white/25 px-4 py-3 text-sm font-sans focus:outline-none focus:border-rdr-gold transition-colors rounded-sm"
                        placeholder="Last name"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-white/50 text-xs uppercase tracking-widest font-sans block mb-2">Email *</label>
                    <input
                      name="email"
                      type="email"
                      required
                      className="w-full bg-white/8 border border-white/15 text-white placeholder-white/25 px-4 py-3 text-sm font-sans focus:outline-none focus:border-rdr-gold transition-colors rounded-sm"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="text-white/50 text-xs uppercase tracking-widest font-sans block mb-2">Phone</label>
                    <input
                      name="phone"
                      type="tel"
                      className="w-full bg-white/8 border border-white/15 text-white placeholder-white/25 px-4 py-3 text-sm font-sans focus:outline-none focus:border-rdr-gold transition-colors rounded-sm"
                      placeholder="(817) 000-0000"
                    />
                  </div>
                  <div>
                    <label className="text-white/50 text-xs uppercase tracking-widest font-sans block mb-2">Event Type *</label>
                    <select
                      name="event_type"
                      required
                      className="w-full bg-white/8 border border-white/15 text-white px-4 py-3 text-sm font-sans focus:outline-none focus:border-rdr-gold transition-colors rounded-sm appearance-none cursor-pointer"
                    >
                      <option value="" className="text-rdr-charcoal">Select event type</option>
                      <option value="Wedding" className="text-rdr-charcoal">Wedding</option>
                      <option value="Private Event" className="text-rdr-charcoal">Private Event</option>
                      <option value="Corporate Event" className="text-rdr-charcoal">Corporate Event</option>
                      <option value="Outdoor Ranch Experience" className="text-rdr-charcoal">Outdoor Ranch Experience</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-white/50 text-xs uppercase tracking-widests font-sans block mb-2">Message</label>
                    <textarea
                      name="message"
                      rows={3}
                      maxLength={500}
                      className="w-full bg-white/8 border border-white/15 text-white placeholder-white/25 px-4 py-3 text-sm font-sans focus:outline-none focus:border-rdr-gold transition-colors rounded-sm resize-none"
                      placeholder="Tell us about your event, date, and guest count..."
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-rdr-gold text-white font-sans text-xs tracking-[0.2em] uppercase py-4 hover:bg-rdr-gold-light transition-colors duration-300 disabled:opacity-60 whitespace-nowrap cursor-pointer mt-2"
                  >
                    {submitting ? 'Sending...' : 'Request a Private Tour'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
